import { ComponentRef } from "@angular/core";
import { FormArray, FormControl } from "@angular/forms";
import { Subject } from "rxjs";
import { SubSink } from "subsink";
import { CheckBoxFormControl } from "./bs-app-launch-checkbox-control/checkbox.control";
import { ResourceChooserFormControl } from "./bs-app-launch-resource-chooser-control/resource-chooser.control";
import { SelectFormControl } from "./bs-app-launch-select-control/select.control";
import { BsAppLaunchFieldSetControlComponent } from "./bs-app-launch-field-set-control/bs-app-launch-field-set-control.component";

export enum UpdateType {
  ADD = 'Add',
  REMOVE = 'Remove'
}

// Form control types used in the app launch form
export enum FormControlType {
  TEXT_BOX = 'TextBox',
  TEXT_AREA = 'TextArea',
  NUMERIC = 'Numeric',
  RADIO_BUTTON = 'RadioButton',
  SELECT = 'Select',
  CHECK_BOX = 'CheckBox',
  FIELD_SET = 'FieldSet',
  TABULAR_FIELD_SET = 'TabularFieldSet',
  SECTION_BREAK = 'SectionBreak',
  // Resource chooser types
  RUN_CHOOSER = 'RunChooser',
  PROJECT_CHOOSER = 'ProjectChooser',
  V1_DATASET_FILE_CHOOSER = 'V1DatasetFileChooser',
  FILE_CHOOSER = 'FileChooser',
  V1_DATASET_CHOOSER = 'V1DatasetChooser',
  APP_RESULT_CHOOSER = 'AppResultChooser',
  SAMPLE_CHOOSER = 'SampleChooser',
  V1_BIOSAMPLE_CHOOSER = 'V1BioSampleChooser'
}

// src/Cloud.Web/Scripts/app/appSessionStart.js line 1085
// Looks like there might be other attributes handled by some default logic
export enum AttributeType {
  ERROR = 'error',
  WARNING = 'warning',
  DISABLED = 'disabled',
  CHECKED = 'checked',
  VALUE = 'value',
  BASE_QUERY = 'base-query',
  IS_OPEN = 'is-open'
}

export enum BasespaceStatus {
    ERROR = 'error',
    WARNING = 'warning'
}

// BASE-88989: when toggler cascading is enabled, a disabled field contributes an empty array
// of values (disabled overrides value), so any stale value sitting on the control doesn't drive
// its togglers. When cascading is off, the disabled state is ignored (legacy behavior).
export function getTogglerValues(control: BasespaceFormControl<any>, cascadingEnabled = false): (string | number)[] {
    if (cascadingEnabled && control.disabled) {
        return [];
    }
    return Array.isArray(control.value) ? control.value : [control.value];
}

export interface BasespaceFormProperties<ComponentType> {
    id: string;
    componentReference?: ComponentRef<ComponentType>;

    value?: any; // Could potentially pass this in as a generic type

    required?: boolean;
    requiredMessage?: string;

    size?: number | string;
    label?: string;
    helpText?: string;

    basespaceErrors?: any[];
    basespaceWarnings?: any[];

    basespaceStatus?: BasespaceStatus;

    // For fields within tabular field set, we conditionally  strip off labels and help text
    // and move them to the header if no default header is provided to increase real estate
    tabularFieldSetStyling?: boolean;

    formControlType?: string;

    // When true, shouldTriggerFormUpdates() will always return false
    disableFormUpdateTriggers?: boolean;

    // For field sets - controls whether the accordion is expanded
    isOpen?: boolean;
}


// Reference docs: https://dagobah-developer.cloud-test.illumina.com/docs/content/documentation/apptools/formbuilder-overview#CommonFieldJSONElements
export class BasespaceFormControl<ComponentType> extends FormControl implements BasespaceFormProperties<ComponentType>{
    // Will usually be same as field unless inside tabular field set
    id: string;

    // Original id passed from field
    fieldId: string;
    componentReference?: ComponentRef<ComponentType>;

    required?: boolean;
    requiredMessage?: string;

    size?: number | string;

    // Used for default width calculation if no size provided from launch form parameters and
    // want to override the default size for base form fields. Good example is for numeric field where
    // we want smaller width than other base fields
    overrideDefaultSize?: number | string;

    label?: string;
    helpText?: string;

    basespaceErrors?: any[] = [];
    basespaceWarnings?: any[] = [];

    tabularFieldSetStyling?: boolean;

    formControlType?: string;

    // When true, shouldTriggerFormUpdates() will always return false
    disableFormUpdateTriggers?: boolean;

    // Track previous value for form update detection
    private previousValue: any;

    // Emits whenever validation state changes (server-side errors/warnings or client-side Angular validators)
    validationChanged$ = new Subject<void>();

    get basespaceStatus(): BasespaceStatus {
        if(this.basespaceErrors.length > 0) {
            return BasespaceStatus.ERROR;
        } else if(this.basespaceWarnings.length > 0) {
            return BasespaceStatus.WARNING;
        } else {
            return null;
        }
    } 

    get width(): string {
        if(this.size) {
            return this.formatSize(this.size);
        } else if(this.overrideDefaultSize) {
            return this.formatSize(this.overrideDefaultSize);
        } else {
            return null;
        }
    }

    formatSize(size: any): string {
        if(!isNaN(size)) {
            return `${size}px`;
        } else {
            return String(size);
        }
    }
    
    protected subs = new SubSink();

    constructor(data: BasespaceFormProperties<ComponentType>) {
        super();
        const { 
            id, 
            componentReference, 
            label, 
            helpText,
            size, 
            required, 
            requiredMessage, 
            tabularFieldSetStyling,
            formControlType
         } = data;

        this.id = id;
        this.tabularFieldSetStyling = tabularFieldSetStyling || false;

        // For acquiring id for a field set within a tabular field set on requeue
        if(id.split('.').length === 3) {
            this.fieldId = id.split('.')[1];
        } else {
            this.fieldId = id;
        }
        
        this.componentReference = componentReference || null;
        this.formControlType = formControlType;

        this.label = label || '';
        this.helpText = helpText || '';
        this.size = size || null;
        this.required = required || false;
        this.requiredMessage = requiredMessage || '';

        this.subs.sink = this.statusChanges.subscribe(() => {
            this.validationChanged$.next();
        });
    }

    updateControl(
        update: { 
        updateType: UpdateType, 
        attributeType: AttributeType; 
        attributeValue: any; 
    }): void {
        const { updateType, attributeType, attributeValue } = update;

        switch(attributeType) {
            case AttributeType.ERROR:
                this.handleErrorUpdate(updateType, attributeValue);
                break;
            case AttributeType.WARNING:
                this.handleWarningUpdate(updateType, attributeValue);
                break;
            case AttributeType.DISABLED:
                this.handleDisabledUpdate(updateType, attributeValue);
                break;
            case AttributeType.CHECKED:
                this.handleCheckedUpdate(updateType, attributeValue);
                break;
            case AttributeType.BASE_QUERY:
                this.handleBaseQueryUpdate(updateType, attributeValue);
                break;
            case AttributeType.VALUE:
                this.handleValueUpdate(updateType, attributeValue);
                break;
            default:
                console.log(`Unimplemented attribute type: '${attributeType}'`)
                break;
        }
    }

    resetBasespaceValidationStatus(): void {
        this.basespaceErrors = [];
        this.basespaceWarnings = [];
        this.validationChanged$.next();
    }


    // Backwards-compatibility: the shouldDisable allowlist below is ported directly from the legacy
    // Cloud.Web app launch form in libs/basespace/src/Cloud.Web/Scripts/app/appSessionStart.js (lines 1083-1089).
    // The legacy code explicitly checks for null, "true", and "disabled" rather than relying on JS truthiness.
    // This matters because some app callbacks (e.g. DRAGEN Enrichment) send Add({ AttributeValue: "false" })
    // to *enable* a field -- a truthy string that the old naive check would incorrectly treat as "disable."
    // Do not simplify this to a plain truthiness check without verifying against the legacy behavior.
    handleDisabledUpdate(updateType: UpdateType, value: string): void {
        const lowerValue = value != null ? String(value).toLowerCase() : null;
        const shouldDisable = updateType === UpdateType.ADD &&
            (lowerValue === null || lowerValue === 'true' || lowerValue === 'disabled');
        if (shouldDisable) {
            this.disable({ emitEvent: false });
        } else {
            this.enable({ emitEvent: false });
        }
        this.validationChanged$.next();
    }

    handleValueUpdate(updateType: UpdateType, value: any): void {
        if(updateType === UpdateType.ADD) {
            switch (this.formControlType) {
                case FormControlType.TEXT_BOX:
                case FormControlType.TEXT_AREA:
                case FormControlType.NUMERIC:
                case FormControlType.RADIO_BUTTON:
                    this.setValue(value, { emitEvent: false });
                    break;
                case FormControlType.SELECT:
                case FormControlType.CHECK_BOX:
                    // Check if value is a JSON array string (starts with '[')
                    // If so, parse it and map values to strings
                    // If it's a primitive, set as-is
                    if (typeof value === 'string' && value.trim().startsWith('[')) {
                        this.setValue(JSON.parse(value).map((v: any) => String(v)), { emitEvent: false });
                    } else {
                        this.setValue(value, { emitEvent: false });
                    }
                    break;
                default:
                    this.setValue(value, { emitEvent: false });
                    break;
            }
        } else {
            this.setValue(null, { emitEvent: false});
        }
    }

    handleErrorUpdate(updateType: UpdateType, value: { message: string; }): void {
        if(updateType === UpdateType.ADD) {
            this.basespaceErrors.push(value);
            this.validationChanged$.next();
        }
    }

    handleWarningUpdate(updateType: UpdateType, value: { message: string; }): void {
        if(updateType === UpdateType.ADD) {
            this.basespaceWarnings.push(value);
            this.validationChanged$.next();
        }
    }

    handleBaseQueryUpdate(updateType: UpdateType, value: string): void {
        if(updateType === UpdateType.ADD) {
            (this as unknown as ResourceChooserFormControl).searchQuery = value;
        } 
        else {
            // Looks like in old UI if you remove the base-query param you wouldn't get prior default but it would be cleared
            (this as unknown as ResourceChooserFormControl).searchQuery = '';
        }
    }

    handleCheckedUpdate(updateType: UpdateType, value: string | number): void {
        if(updateType === UpdateType.ADD) {
            // Logic taken from src/Cloud.Web/Scripts/app/appSessionStart.js line 1086
            const checked = value === null || value === "true" || value === "checked";
            if(checked) {
                 const updatedValue = (this as unknown as CheckBoxFormControl).choices.map((choice) => {
                    choice.checked = true;
                    return choice.value
                });
                 this.setValue(updatedValue);
            } else {
                (this as unknown as CheckBoxFormControl).choices.forEach((choice) => choice.checked = false);
                this.setValue([]);
            }
        } else {
            (this as unknown as CheckBoxFormControl).choices.forEach((choice) => choice.checked = false);
            this.setValue([]);
        }
    }

    getRequestEntries(): any[][] {
        return [[this.id, this.value]];
    }

    // Capture the current value to track for form update triggers
    trackValueForFormUpdates(): void {
        this.previousValue = this.value;
    }

    // Determine if the value has changed enough to trigger form updates
    // Handles primitive values, arrays, and objects
    shouldTriggerFormUpdates(): boolean {
        // If form update triggers are disabled, always return false
        if (this.disableFormUpdateTriggers) {
            return false;
        }

        const currentValue = this.value;
        const previousValue = this.previousValue;

        // If previousValue was never set, no change has occurred yet
        if (previousValue === undefined) {
            return false;
        }

        // For primitive types (string, number, boolean, null)
        if (!Array.isArray(currentValue) && !Array.isArray(previousValue) 
            && typeof currentValue !== 'object' && typeof previousValue !== 'object') {
            return previousValue !== currentValue;
        }

        // For arrays and objects, use JSON comparison
        return JSON.stringify(previousValue) !== JSON.stringify(currentValue);
    }

    // Removes the control from the parent and destroys the component reference (removes it from display)
    destroy(): void {
        // This should always be true. Children can be FormArrays or FormControls but parents will always be FormArrays
        // to keep things consistent.
        const parentControl = this.parent as FormArray; 
        const index = (parentControl.controls).findIndex((control) => (control as BasespaceFormControl<ComponentType>).id === this.id);
        parentControl.removeAt(index);
        this.validationChanged$.complete();
        this.subs.unsubscribe();
        if(this.componentReference) {
            this.componentReference.destroy();
        }
    }
}


export class BasespaceFormArray<ComponentType> extends FormArray implements BasespaceFormProperties<ComponentType>{
    id: string;
    componentReference?: ComponentRef<ComponentType>;

    label?: string;
    helpText?: string;

    formControlType?: string;

    // For field sets - controls whether the accordion is expanded
    isOpen?: boolean;
    
    protected subs = new SubSink();

    constructor(data: BasespaceFormProperties<ComponentType>) {
        super([]);
        const { id, componentReference, label, helpText, required, requiredMessage, formControlType, isOpen } = data;

        this.id = id;
        this.componentReference = componentReference || null;

        this.label = label || '';
        this.helpText = helpText || '';

        this.formControlType = formControlType;
        this.isOpen = isOpen || false;

    }

    updateControl(
        update: { 
        updateType: UpdateType, 
        attributeType: AttributeType; 
        attributeValue: any; 
    }): void {
        const { updateType, attributeType, attributeValue } = update;

        switch(attributeType) {
            case AttributeType.IS_OPEN:
                this.handleIsOpenUpdate(updateType, attributeValue);
                break;
            default:
                console.log(`Unimplemented attribute type for FormArray: '${attributeType}'`)
                break;
        }
    }

    /**
     * Handles the IS_OPEN attribute update for field sets.
     * Opens the field set if updateType is ADD and value is truthy.
     * Closes the field set if updateType is REMOVE or value is falsy.
     */
    handleIsOpenUpdate(updateType: UpdateType, value: any): void {
        if (updateType === UpdateType.ADD && value) {
            // Parse the value if it's a string boolean
            const isOpen = typeof value === 'string' ? value.toLowerCase() === 'true' : !!value;
            this.isOpen = isOpen;
        } else {
            // REMOVE or falsy value closes the field set
            this.isOpen = false;
        }

        // Try to access the accordion element directly and set expanded property
        if (this.componentReference && (this.componentReference.instance as unknown as BsAppLaunchFieldSetControlComponent).accordion) {
            const accordionElement = (this.componentReference.instance as any).accordion.nativeElement;
            if (accordionElement) {
                accordionElement.setAttribute('expanded', String(this.isOpen));
            }
        }
    }

    // Destroys all children and then destroys self
    destroy(): void {
        this.controls.forEach((control: BasespaceFormArray<any> | BasespaceFormControl<any>) => {
            control.destroy();
        });

        // This should always be true. Children can be FormArrays or FormControls but parents will always be FormArrays
        // to keep things consistent.
        const parentControl = this.parent as FormArray;
        if(parentControl) {
            const index = (parentControl.controls).findIndex((control) => (control as BasespaceFormControl<ComponentType>).id === this.id);
            parentControl.removeAt(index);
        }
        
        this.subs.unsubscribe();
        this.componentReference.destroy();
    }
}

